import { useState, useEffect } from "react";
import { collection, addDoc, query, where, getDocs, deleteDoc, doc, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { FaSave, FaTrash } from "react-icons/fa";

const CouponCodes = () => {
    const [couponValue, setCouponValue] = useState("");
    const [couponCode, setCouponCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });
    const [coupons, setCoupons] = useState([]);
    const [loadingCoupons, setLoadingCoupons] = useState(true);
    const [deletingId, setDeletingId] = useState(null);

    // Fetch all coupon codes
    const fetchCoupons = async () => {
        try {
            setLoadingCoupons(true);
            const couponsQuery = query(
                collection(db, "couponCodes"),
                orderBy("createdAt", "desc")
            );
            const querySnapshot = await getDocs(couponsQuery);
            const couponsList = [];
            querySnapshot.forEach((doc) => {
                couponsList.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            setCoupons(couponsList);
        } catch (error) {
            console.error("Error fetching coupon codes:", error);
            setMessage({ type: "error", text: "Failed to load coupon codes" });
            setTimeout(() => setMessage({ type: "", text: "" }), 3000);
        } finally {
            setLoadingCoupons(false);
        }
    };

    useEffect(() => {
        fetchCoupons();
    }, []);

    const handleSave = async () => {
        // Validation
        if (!couponValue.trim() || isNaN(couponValue) || parseFloat(couponValue) <= 0) {
            setMessage({ type: "error", text: "Please enter a valid coupon value (number greater than 0)" });
            setTimeout(() => setMessage({ type: "", text: "" }), 3000);
            return;
        }

        if (!couponCode.trim()) {
            setMessage({ type: "error", text: "Please enter a coupon code" });
            setTimeout(() => setMessage({ type: "", text: "" }), 3000);
            return;
        }

        // Check if code already exists
        try {
            setLoading(true);
            const codeQuery = query(
                collection(db, "couponCodes"),
                where("code", "==", couponCode.trim().toUpperCase())
            );
            const codeSnapshot = await getDocs(codeQuery);

            if (!codeSnapshot.empty) {
                setMessage({ type: "error", text: "This coupon code already exists. Please use a unique code." });
                setTimeout(() => setMessage({ type: "", text: "" }), 3000);
                setLoading(false);
                return;
            }

            // Save to Firebase
            const couponData = {
                value: parseFloat(couponValue),
                code: couponCode.trim().toUpperCase(),
                createdAt: new Date().toISOString(),
            };

            await addDoc(collection(db, "couponCodes"), couponData);

            // Success
            setMessage({ type: "success", text: "Coupon code saved successfully!" });
            setCouponValue("");
            setCouponCode("");
            setTimeout(() => setMessage({ type: "", text: "" }), 3000);
            // Refresh the list
            fetchCoupons();
        } catch (error) {
            console.error("Error saving coupon code:", error);
            setMessage({ type: "error", text: "Failed to save coupon code. Please try again." });
            setTimeout(() => setMessage({ type: "", text: "" }), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (couponId, couponCode) => {
        if (!window.confirm(`Are you sure you want to delete coupon code "${couponCode}"?`)) {
            return;
        }

        try {
            setDeletingId(couponId);
            await deleteDoc(doc(db, "couponCodes", couponId));
            setMessage({ type: "success", text: "Coupon code deleted successfully!" });
            setTimeout(() => setMessage({ type: "", text: "" }), 3000);
            // Refresh the list
            fetchCoupons();
        } catch (error) {
            console.error("Error deleting coupon code:", error);
            setMessage({ type: "error", text: "Failed to delete coupon code. Please try again." });
            setTimeout(() => setMessage({ type: "", text: "" }), 3000);
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="coupon-codes-panel">
            <style>{`
        .coupon-codes-panel {
          padding: 30px;
          margin: 0 auto;
          background: var(--bg-primary, #f9fafb);
          border-radius: 8px;
          box-shadow: 0 2px 4px var(--shadow, rgba(0,0,0,0.05));
          max-width: 1200px;
        }
        .form-section {
          background: var(--bg-card, #ffffff);
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 30px;
          box-shadow: 0 2px 4px var(--shadow, rgba(0,0,0,0.1));
        }
        .list-section {
          background: var(--bg-card, #ffffff);
          padding: 20px;
          border-radius: 8px;
          box-shadow: 0 2px 4px var(--shadow, rgba(0,0,0,0.1));
        }
        .section-title {
          color: var(--text-primary, #1f2937);
          font-size: 20px;
          margin: 0 0 20px 0;
          padding-bottom: 10px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }
        h2 {
          color: var(--text-primary, #1f2937);
          font-size: 24px;
          margin: 0 0 30px 0;
          padding-bottom: 15px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }
        .form-group {
          margin-bottom: 20px;
        }
        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: var(--text-primary, #374151);
          font-size: 14px;
        }
        .form-group input {
          width: 100%;
          padding: 12px;
          border: 1px solid var(--border-color, #d1d5db);
          border-radius: 6px;
          font-size: 14px;
          background: var(--bg-input, #ffffff);
          color: var(--text-primary, #111827);
          box-sizing: border-box;
        }
        .form-group input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
        }
        .btn {
          padding: 12px 24px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s;
          width: 100%;
        }
        .btn-primary {
          background: #3b82f6;
          color: white;
        }
        .btn-primary:hover:not(:disabled) {
          background: #2563eb;
        }
        .btn-primary:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }
        .btn-danger {
          background: #ef4444;
          color: white;
          width: auto;
          padding: 8px 16px;
        }
        .btn-danger:hover:not(:disabled) {
          background: #dc2626;
        }
        .btn-danger:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }
        .table-container {
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          background: var(--bg-card, #ffffff);
        }
        th {
          background: #374151;
          color: #ffffff;
          padding: 12px;
          text-align: left;
          font-weight: 600;
          font-size: 14px;
          border-bottom: 2px solid var(--border-color, #4b5563);
        }
        [data-theme="dark"] th {
          background: #1f2937;
          color: #ffffff;
          border-bottom-color: var(--border-color, #404040);
        }
        td {
          padding: 12px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
          font-size: 14px;
          color: var(--text-primary, #4a5568);
          background: var(--bg-card, #ffffff);
        }
        tr {
          background: var(--bg-card, #ffffff);
        }
        [data-theme="dark"] tr {
          background: var(--bg-secondary, #2d2d2d);
        }
        tr:hover {
          background: var(--bg-hover, #f9fafb);
        }
        [data-theme="dark"] tr:hover {
          background: var(--bg-hover, #353535);
        }
        .empty-state {
          padding: 40px;
          text-align: center;
          color: var(--text-muted, #6b7280);
        }
        .empty-state h3 {
          color: var(--text-primary, #1f2937);
          margin-bottom: 10px;
        }
        .loading-state {
          padding: 40px;
          text-align: center;
          color: var(--text-muted, #6b7280);
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .loading-state .loader {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: inline-block;
          position: relative;
          border: 2px solid #FF3D00;
          box-sizing: border-box;
          animation: rotation 1s linear infinite;
        }
        .loading-state .loader::after {
          content: '';  
          box-sizing: border-box;
          position: absolute;
          left: 4px;
          top: 4px;
          border: 2px solid #000;
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }
        [data-theme="dark"] .loading-state .loader::after {
          border: 2px solid #FFF;
        }
        @keyframes rotation {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .message {
          padding: 12px 16px;
          border-radius: 6px;
          margin-bottom: 20px;
          font-size: 14px;
        }
        .message-success {
          background: #d1fae5;
          color: #065f46;
          border: 1px solid #10b981;
        }
        .message-error {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #ef4444;
        }
        @media (max-width: 768px) {
          .coupon-codes-panel {
            padding: 20px;
          }
        }
      `}</style>

            <h2>Coupon Codes</h2>

            {message.text && (
                <div className={`message ${message.type === "success" ? "message-success" : "message-error"}`}>
                    {message.text}
                </div>
            )}

            <div className="form-section">
                <h3 className="section-title">Add New Coupon Code</h3>
                <div className="form-group">
                    <label htmlFor="couponValue">Coupon Value</label>
                    <input
                        id="couponValue"
                        type="number"
                        placeholder="Enter coupon value (e.g., 100)"
                        value={couponValue}
                        onChange={(e) => setCouponValue(e.target.value)}
                        min="0"
                        step="0.01"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="couponCode">Coupon Code</label>
                    <input
                        id="couponCode"
                        type="text"
                        placeholder="Enter unique coupon code (e.g., SAVE100)"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        style={{ textTransform: "uppercase" }}
                    />
                </div>

                <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={loading}
                >
                    <FaSave /> {loading ? "Saving..." : "Save"}
                </button>
            </div>

            <div className="list-section">
                <h3 className="section-title">Active Coupon Codes</h3>
                {loadingCoupons ? (
                    <div className="loading-state">
                        <span className="loader"></span>
                    </div>
                ) : coupons.length === 0 ? (
                    <div className="empty-state">
                        <h3>No Coupon Codes Found</h3>
                        <p>Add your first coupon code using the form above.</p>
                    </div>
                ) : (
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th style={{ width: "50px" }}>S.No</th>
                                    <th>Coupon Code</th>
                                    <th>Value</th>
                                    <th>Created At</th>
                                    <th style={{ width: "100px" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {coupons.map((coupon, index) => (
                                    <tr key={coupon.id}>
                                        <td>{index + 1}</td>
                                        <td style={{ fontWeight: "600" }}>{coupon.code}</td>
                                        <td>₹{coupon.value}</td>
                                        <td>
                                            {coupon.createdAt
                                                ? new Date(coupon.createdAt).toLocaleDateString("en-IN", {
                                                      year: "numeric",
                                                      month: "short",
                                                      day: "numeric",
                                                      hour: "2-digit",
                                                      minute: "2-digit",
                                                  })
                                                : "N/A"}
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn-danger"
                                                onClick={() => handleDelete(coupon.id, coupon.code)}
                                                disabled={deletingId === coupon.id}
                                                title="Delete"
                                            >
                                                <FaTrash /> {deletingId === coupon.id ? "..." : ""}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CouponCodes;

